import React, { useEffect, useState } from "react";
import "./style/fullName.scss";
import { Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import moment from "moment";
import { showError } from "../../services/toastservice/toastService";
import { touchlessService } from "../../services/api/touchlessService";
import { setCookie } from "../../utils/helpers";
import LoadingSpinner from "../../components/ui/Spinner/LoadingSpinner";
import { BackButton } from "../../components/ui/Button/backButton";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { useStrings } from "../../hooks/useStrings";
import { ROUTES } from "../../utils/constants";

interface FormData {
  fullName: string;
}

const FullName: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const { config } = useAppConfig();
  const hasContactlessLookup =
    config?.orgProducts?.products?.[0]?.entitlements
      ?.some((e) => e.key === "CONTACTLESS_SIGNOUT_LOOKUP" && e.value === "true") ?? false;
  const {
    visitorFullName,
    updateVisitorFullName,
    updatePreRegisterData,
    updateOnSiteVisitors,
  } = useVisitorData();
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      fullName: "",
    },
  });


  useEffect(() => {
    if (visitorFullName) {
      setValue("fullName", visitorFullName, { shouldValidate: true });
    }
  }, [visitorFullName]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data: FormData) => {
    const name = data.fullName.trim();
    setIsLoading(true);
    try {
      const [onSiteResult, preRegResult] = await Promise.allSettled([
        hasContactlessLookup && config?.siteId && config?.orgId
          ? touchlessService.getOnSiteVisitors(name, config.siteId, config.orgId)
          : Promise.resolve([]),
        touchlessService.getPreRegisterVisitor(name),
      ]);

      const onSite = onSiteResult.status === "fulfilled" ? onSiteResult.value : [];
      const preReg = preRegResult.status === "fulfilled" ? preRegResult.value : null;
      const preRegResults = (preReg as { data?: { results?: unknown[] } })?.data?.results ?? [];

      const hasOnSite = onSite.length > 0;
      const hasPreReg = preRegResults.length > 0;

      updateVisitorFullName(name);

      // Scenario 1: on-site only → set cookies, straight to /signout
      if (hasOnSite && !hasPreReg) {
        const v = onSite[0];
        // Clear any stale Redux state from previous runs
        updateOnSiteVisitors(null);
        updatePreRegisterData(null);
        setCookie("visitId",        v.id,                                                    3);
        setCookie("visitorName",    v.fullName,                                               3);
        setCookie("orgName",        v.orgName   ?? config?.orgName   ?? "",                   3);
        setCookie("siteName",       v.siteName  ?? config?.siteName  ?? "",                   3);
        setCookie("signInDateTime",
          v.checkinTime ? moment(v.checkinTime).format("DD MMM YY hh:mm A") : "",            3);
        if (v.visitorTypeId) setCookie("visitorTypeId", v.visitorTypeId, 3);
        if (v.hostName)      setCookie("hostName",      v.hostName,      3);
        navigate(`/${ROUTES.SIGN_OUT}`, { replace: true });
        return;
      }

      // Scenario 2: pre-reg only → same pre-reg flow
      if (hasPreReg && !hasOnSite) {
        updateOnSiteVisitors(null); // Clear any stale on-site state from previous runs
        updatePreRegisterData(preRegResults[0] as any);
        navigate(`/${ROUTES.PRE_REG_CONFIRMATION}`);
        return;
      }

      // Scenario 3: both → merged choice page
      if (hasOnSite && hasPreReg) {
        updateOnSiteVisitors(onSite);
        updatePreRegisterData(preRegResults[0] as any);
        navigate(`/${ROUTES.PRE_REG_CONFIRMATION}`);
        return;
      }

      // Neither → regular sign-in
      navigate(`/${ROUTES.VISITOR_TYPES}`);
    } catch {
      showError(S.VALIDATION.TOAST_ERROR_PROCESSING);
    } finally {
      setIsLoading(false);
    }
  };

  const onBack = () => {
    if (config?.privacyConsentType) {
      navigate("/privacy-policy");
    } else {
      navigate("/");
    }
  };

  const validationRules = {
    required: S.VALIDATION.FULL_NAME_REQUIRED,
    pattern: {
      value: /^[\p{L}\p{M}\p{Zs}.''-]+$/u,
      message: S.VALIDATION.FULL_NAME_INVALID,
    },
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="main-screen" data-testid="full-name-main-screen">
      <div
        className="screen-outer fullname-screen"
        data-testid="full-name-screen-outer"
      >
        <div
          className="screen-header-fixed"
          data-testid="full-name-header-fixed"
        >
          <div
            className="header-fixed-left"
            data-testid="full-name-header-left"
          >
            <BackButton onBack={onBack} />
          </div>
          <div
            className="header-fixed-right"
            data-testid="full-name-header-right"
          ></div>
        </div>

        <div className="screen-inner" data-testid="full-name-screen-inner">
          <div
            className="screen-subinner"
            data-testid="full-name-screen-subinner"
          >
            <div
              className="screen-header"
              data-testid="full-name-screen-header"
            ></div>

            <div className="screen-body" data-testid="full-name-screen-body">
              <h5 className="screen-title" data-testid="visitor-type-title">
                {config?.nameFieldLabel || S.FULL_NAME.TITLE}
              </h5>
              <p
                className="screen-subtitle"
                data-testid="visitor-type-subtitle"
              >
                {S.FULL_NAME.SUBTITLE}
              </p>
              <div data-testid="full-name-form">
                <div
                  className="form-group fg-lg"
                  data-testid="full-name-form-group"
                >
                  <Form.Label
                    className="form-label"
                    data-testid="full-name-label"
                    name="fullName"
                  >
                    {S.FULL_NAME.FORM_LABEL}
                  </Form.Label>

                  <div className="d-flex align-items-center form-control">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ color: "var(--primary-btn-color-muted)" }}
                    >
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    <Controller
                      name="fullName"
                      control={control}
                      rules={validationRules}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Form.Control
                          type="text"
                          value={value}
                          onChange={onChange}
                          onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            onChange(trimmedValue);
                            onBlur();
                          }}
                          placeholder={S.FULL_NAME.PLACEHOLDER}
                          data-testid="full-name-input"
                        />
                      )}
                    />
                  </div>

                  {errors.fullName && (
                    <p className="error-msg" data-testid="full-name-error">
                      {errors.fullName.message}
                    </p>
                  )}
                </div>

                <div className="mt-5" data-testid="full-name-submit-wrapper">
                  <Button
                    type="button"
                    className="btn btn-primary btn-shadow btn-width-full"
                    disabled={isLoading || !isValid}
                    onClick={handleSubmit(onSubmit)}
                    data-testid="full-name-next-button"
                  >
                    {S.COMMON.CONTINUE} &nbsp;&nbsp;
                    <i className="bi bi-arrow-right-short"></i>
                  </Button>
                </div>
              </div>
            </div>

            <div
              className="screen-qrcode mt-5"
              data-testid="full-name-qrcode-section"
            >
              {/* QR code section - currently empty in Angular version */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullName;
